import type { FinancePartnerRow, FinancingProduct, PriceCell } from '../../../../store/oneButtonStore';
import {
    getRecommendedPicks,
    partnerLabelForCombined,
    type CombinedFinancePick,
    type CombinedFinancingSummary,
} from './financingCombinedSummary';
import { effectiveAnnualUsageKwhForPricing } from '../../../../helpers/utilityMonthlyDailyToAnnual';

export type FinancingAiPlanTone = 'cash' | 'lightreach' | 'goodleap' | 'igs' | 'enfin';

export type FinancingAiPlanMetric = {
    label: string;
    value: string;
    tone?: 'positive' | 'neutral' | 'credit';
};

export type FinancingAiAddon = {
    id: string;
    title: string;
    subtitle: string;
    monthly: number;
    isCash: boolean;
    included: boolean;
    cellKey?: string;
    partner: string;
    /** False when this combined line uses a different finance partner than the selected plan. */
    matchesSelectedPlan: boolean;
    /** Products this line pays for — drives the covered-product label on the selected plan. */
    productTags: FinancingProduct[];
};

function formatAddonPrice(addon: FinancingAiAddon): string {
    if (addon.isCash) {
        return formatMoney(addon.monthly, 0);
    }
    return `${formatMoney(addon.monthly)}/mo`;
}

export type FinancingAiPlan = {
    id: string;
    rank: number;
    tone: FinancingAiPlanTone;
    badgeLabel: string;
    aiScore: number;
    title: string;
    subtitle: string;
    monthly: number;
    isCash: boolean;
    cashPrice?: number;
    escalatorPct: number | null;
    aprPct: number | null;
    termYears: number | null;
    financeMethod: string;
    metrics: FinancingAiPlanMetric[];
    insightTags: string[];
    billToday: number | null;
    electricAfter: number | null;
    fullPlanMonthly: number;
    cumulativeDoNothing: number | null;
    cumulativeThisPlan: number | null;
    monthlySavings: number | null;
    year1Savings: number | null;
    savings25yr: number | null;
    creditLabel: string;
    cellKey: string;
    partner: string;
    product: FinancingProduct;
    /** Portion of the monthly payment compared against the utility bill (roof financing excluded). */
    savingsBasisMonthly: number;
    /** Monthly payment that buys no electric savings (roof financing). */
    nonEnergyMonthly: number;
};

const PARTNER_PLAN_ORDER: { partner: string; tone: FinancingAiPlanTone; badge: string; aiScore: number }[] = [
    { partner: 'Cash', tone: 'cash', badge: 'CASH', aiScore: 100 },
    { partner: 'LightReach', tone: 'lightreach', badge: 'LIGHTREACH', aiScore: 80 },
    { partner: 'GoodLeap', tone: 'goodleap', badge: 'GOODLEAP', aiScore: 70 },
    { partner: 'IGS', tone: 'igs', badge: 'IGS', aiScore: 65 },
    { partner: 'Enfin', tone: 'enfin', badge: 'ENFIN', aiScore: 60 },
];

const PRODUCT_PRIORITY: FinancingProduct[] = [
    'Solar',
    'Solar + Roof',
    'Solar + Battery',
    'Roof',
    'Battery',
    'Battery + Roof',
];

export type BuildFinancingAiPlansInput = {
    rows: FinancePartnerRow[];
    combinedSummary: CombinedFinancingSummary;
    utilityBillMonthly: number | null;
};

function formatMoney(value: number, decimals = 2): string {
    return `$${value.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}`;
}

function formatCompactK(value: number): string {
    if (value >= 1000) return `$${Math.round(value / 1000)}k`;
    return formatMoney(value, 0);
}

function cellDisplayMonthly(cell: PriceCell, isCash: boolean): number {
    if (isCash) {
        const purchase = cell.purchasePrice ?? cell.monthly;
        return Number.isFinite(purchase) && purchase > 0 ? purchase : cell.monthly;
    }
    return cell.monthly;
}

function formatCellDetail(cell: PriceCell, isCash: boolean): string {
    if (isCash) return 'One-time payment from pricing grid';
    const parts: string[] = [];
    if (cell.escalator != null && cell.escalator > 0) parts.push(`${cell.escalator}% escalator`);
    else if (cell.apr != null && cell.apr > 0) parts.push(`${cell.apr}% APR`);
    if (cell.term > 0) parts.push(`${cell.term} yr`);
    if (cell.financeMethod) parts.push(cell.financeMethod);
    return parts.join(' · ') || 'Financed option from pricing grid';
}

function insightTagsFromCell(cell: PriceCell, isCash: boolean): string[] {
    if (isCash) return ['One-time payment'];
    const tags: string[] = [];
    if (cell.recommended) tags.push('Recommended');
    if (cell.escalator != null && cell.escalator === 0) tags.push('0% escalator');
    else if (cell.escalator != null && cell.escalator > 0) tags.push(`${cell.escalator}% escalator`);
    if (cell.apr != null && cell.apr > 0) tags.push(`${cell.apr}% APR`);
    if (cell.financeMethod) tags.push(cell.financeMethod);
    return tags;
}

function findPartnerPrimaryPick(rows: FinancePartnerRow[], partner: string): CombinedFinancePick | null {
    const row = rows.find((r) => r.partner === partner);
    if (!row) return null;
    const isCash = partner === 'Cash';

    for (const product of PRODUCT_PRIORITY) {
        const cell = row.prices[product];
        if (!cell?.recommended) continue;
        const monthly = cellDisplayMonthly(cell, isCash);
        if (!Number.isFinite(monthly) || monthly <= 0) continue;
        return { cellKey: `${partner}::${product}`, partner, product, cell, isCash };
    }

    for (const product of PRODUCT_PRIORITY) {
        const cell = row.prices[product];
        if (!cell) continue;
        const monthly = cellDisplayMonthly(cell, isCash);
        if (!Number.isFinite(monthly) || monthly <= 0) continue;
        return { cellKey: `${partner}::${product}`, partner, product, cell, isCash };
    }

    return null;
}

function savingsFromBill(planMonthly: number, billToday: number | null) {
    if (billToday == null || billToday <= 0) {
        return {
            monthlySavings: null,
            year1Savings: null,
            savings25yr: null,
            electricAfter: null,
            cumulativeDoNothing: null,
            cumulativeThisPlan: null,
        };
    }

    const monthlySavings = Math.max(0, billToday - planMonthly);
    const year1Savings = monthlySavings * 12;
    const savings25yr = year1Savings * 25;
    const electricAfter = Math.max(0, billToday - monthlySavings);
    const cumulativeDoNothing = billToday * 12 * 25;
    const cumulativeThisPlan = planMonthly * 12 * 25;

    return {
        monthlySavings,
        year1Savings,
        savings25yr,
        electricAfter,
        cumulativeDoNothing,
        cumulativeThisPlan,
    };
}

/**
 * Only solar / battery lines displace utility usage. Roof financing is a home-improvement cost, so
 * it must stay out of any electric-bill savings comparison.
 */
export function productOffsetsUtilityBill(product: FinancingProduct): boolean {
    return atomicProductParts(product).some((part) => {
        const p = part.toLowerCase();
        return p === 'solar' || p === 'battery';
    });
}

/** Roof-only financing: keep the bill/cost figures, but report no electric savings. */
function savingsWithoutBillOffset(planMonthly: number, billToday: number | null) {
    return {
        monthlySavings: null,
        year1Savings: null,
        savings25yr: null,
        electricAfter: billToday,
        cumulativeDoNothing: billToday != null && billToday > 0 ? billToday * 12 * 25 : null,
        cumulativeThisPlan: planMonthly * 12 * 25,
    };
}

function planTitleForPick(pick: CombinedFinancePick): string {
    const partnerLabel = partnerLabelForCombined(pick.partner);
    if (pick.isCash) return 'Cash purchase';
    if (pick.product === 'Solar') return `${partnerLabel} solar ${pick.cell.financeMethod || 'PPA'}`;
    return `${partnerLabel} — ${pick.product}`;
}

function planSubtitleForPick(pick: CombinedFinancePick): string {
    return formatCellDetail(pick.cell, pick.isCash);
}

function buildPlanFromPick(
    pick: CombinedFinancePick,
    rank: number,
    tone: FinancingAiPlanTone,
    badgeLabel: string,
    aiScore: number,
    utilityBillMonthly: number | null,
): FinancingAiPlan {
    const monthly = cellDisplayMonthly(pick.cell, pick.isCash);
    /** Compare this plan’s monthly outflow to the utility bill — not the combined grid total. Cash has no monthly payment. */
    const savingsPayment = pick.isCash ? 0 : monthly;
    const offsetsBill = productOffsetsUtilityBill(pick.product);
    const savings = offsetsBill
        ? savingsFromBill(savingsPayment, utilityBillMonthly)
        : savingsWithoutBillOffset(savingsPayment, utilityBillMonthly);

    const savingsMetrics: FinancingAiPlanMetric[] = offsetsBill
        ? [
              {
                  label: 'Year-1 save',
                  value: savings.year1Savings != null ? formatMoney(savings.year1Savings, 0) : '—',
                  tone: savings.year1Savings != null ? 'positive' : 'neutral',
              },
              {
                  label: '25-yr save',
                  value:
                      savings.savings25yr != null
                          ? pick.isCash
                              ? formatMoney(savings.savings25yr, 0)
                              : formatCompactK(savings.savings25yr)
                          : '—',
                  tone: savings.savings25yr != null ? 'positive' : 'neutral',
              },
          ]
        : [
              {
                  label: 'Term',
                  value: pick.cell.term > 0 ? `${pick.cell.term} yr` : '—',
              },
              {
                  label: 'Rate',
                  value:
                      pick.cell.apr != null && pick.cell.apr > 0
                          ? `${pick.cell.apr}% APR`
                          : pick.cell.escalator != null && pick.cell.escalator > 0
                            ? `${pick.cell.escalator}% escalator`
                            : '—',
              },
          ];

    const metrics: FinancingAiPlanMetric[] = pick.isCash
        ? [{ label: 'Cash price', value: formatMoney(monthly, 0) }, ...savingsMetrics]
        : [{ label: 'Monthly', value: `${formatMoney(monthly)}/mo` }, ...savingsMetrics];

    return {
        id: pick.cellKey,
        rank,
        tone,
        badgeLabel,
        aiScore: pick.cell.recommended ? aiScore : Math.max(0, aiScore - 15),
        title: planTitleForPick(pick),
        subtitle: planSubtitleForPick(pick),
        monthly,
        isCash: pick.isCash,
        cashPrice: pick.isCash ? monthly : undefined,
        escalatorPct: pick.isCash ? null : pick.cell.escalator ?? null,
        aprPct: pick.isCash ? null : pick.cell.apr ?? null,
        termYears: pick.isCash ? null : pick.cell.term > 0 ? pick.cell.term : null,
        financeMethod: pick.cell.financeMethod ?? '',
        metrics,
        insightTags: offsetsBill
            ? insightTagsFromCell(pick.cell, pick.isCash)
            : ['No electric bill savings', ...insightTagsFromCell(pick.cell, pick.isCash)],
        billToday: utilityBillMonthly,
        fullPlanMonthly: savingsPayment,
        savingsBasisMonthly: offsetsBill ? savingsPayment : 0,
        nonEnergyMonthly: offsetsBill ? 0 : savingsPayment,
        creditLabel: pick.isCash ? 'No check' : pick.cell.financeMethod?.toLowerCase().includes('loan') ? 'Soft check' : 'No check',
        cellKey: pick.cellKey,
        partner: pick.partner,
        product: pick.product,
        ...savings,
    };
}

function parsePositiveNumber(value: unknown): number | null {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
}

/** Monthly utility bill from SolarUtility / Salesforce usage data. */
export function resolveUtilityBillMonthly(
    solarUtilityData: unknown,
    sfAnnualUsage: number | null | undefined,
): number | null {
    const raw = (solarUtilityData ?? null) as Record<string, unknown> | null;
    const su = ((raw as { SolarUtility?: Record<string, unknown> } | null)?.SolarUtility ??
        raw) as Record<string, unknown> | null | undefined;

    const billMonth = parsePositiveNumber(su?.ElectricBillMonth ?? su?.electricBillMonth);
    if (billMonth != null) return billMonth;

    const annualAmt = parsePositiveNumber(su?.AnnualAmount ?? su?.annualAmount);
    if (annualAmt != null) return annualAmt / 12;

    const allInRate = parsePositiveNumber(
        su?.CurrentAllInUtilityRate ??
            su?.currentAllInUtilityRate ??
            raw?.CurrentAllInUtilityRate ??
            raw?.currentAllInUtilityRate,
    );
    const annualUsage = effectiveAnnualUsageKwhForPricing(su ?? raw, sfAnnualUsage);
    if (allInRate != null && annualUsage != null && annualUsage > 0) {
        return (allInRate * annualUsage) / 12;
    }

    return null;
}

/** Combined pricing lines — same source as the Combined Financing tab (`combinedSummary.lines`). */
export function buildFinancingAiCombinedProducts(
    combinedSummary: CombinedFinancingSummary,
    selectedPlan: FinancingAiPlan | null | undefined,
): FinancingAiAddon[] {
    const selectedPartner = selectedPlan?.partner ?? '';

    return combinedSummary.lines.map((line) => {
        const partnerDiffersFromSelected =
            selectedPartner !== '' &&
            selectedPartner !== 'Custom' &&
            line.partner !== selectedPartner;

        return {
            id: line.id,
            title: `${line.partnerLabel}${line.method ? ` · ${line.method}` : ''}`,
            subtitle: `${line.productLabel}${line.detail ? ` · ${line.detail}` : ''}`,
            monthly: line.monthly,
            isCash: false,
            included: true,
            cellKey: line.id,
            partner: line.partner,
            matchesSelectedPlan: !partnerDiffersFromSelected,
            productTags: line.productTags,
        };
    });
}

/** @deprecated Use buildFinancingAiCombinedProducts */
export function buildFinancingAiAddonsFromGrid(
    rows: FinancePartnerRow[],
    combinedSummary?: CombinedFinancingSummary,
    selectedPlan?: FinancingAiPlan | null,
): FinancingAiAddon[] {
    if (combinedSummary && combinedSummary.lines.length > 0) {
        return buildFinancingAiCombinedProducts(combinedSummary, selectedPlan);
    }

    const picks = getRecommendedPicks(rows);
    const batteryPick = picks.find((p) => p.product === 'Battery');
    const roofPick = picks.find((p) => p.product === 'Roof');
    const addons: FinancingAiAddon[] = [];

    if (batteryPick) {
        addons.push({
            id: 'battery',
            title: 'Battery',
            subtitle: `${partnerLabelForCombined(batteryPick.partner)} · ${batteryPick.cell.financeMethod || 'Battery'}`,
            monthly: cellDisplayMonthly(batteryPick.cell, batteryPick.isCash),
            isCash: batteryPick.isCash,
            included: true,
            cellKey: batteryPick.cellKey,
            partner: batteryPick.partner,
            matchesSelectedPlan: true,
            productTags: [batteryPick.product],
        });
    }

    if (roofPick) {
        addons.push({
            id: 'roof',
            title: 'Roof',
            subtitle: `${partnerLabelForCombined(roofPick.partner)} · ${roofPick.cell.financeMethod || 'Roof'}`,
            monthly: roofPick.cell.monthly,
            isCash: roofPick.isCash,
            included: true,
            cellKey: roofPick.cellKey,
            partner: roofPick.partner,
            matchesSelectedPlan: true,
            productTags: [roofPick.product],
        });
    }

    return addons;
}

export function buildFinancingAiPlans(input: BuildFinancingAiPlansInput): FinancingAiPlan[] {
    const { rows, utilityBillMonthly } = input;

    const plans: FinancingAiPlan[] = [];
    let rank = 1;

    for (const spec of PARTNER_PLAN_ORDER) {
        const pick = findPartnerPrimaryPick(rows, spec.partner);
        if (!pick) continue;
        plans.push(
            buildPlanFromPick(
                pick,
                rank++,
                spec.tone,
                spec.badge,
                spec.aiScore,
                utilityBillMonthly,
            ),
        );
    }

    return plans;
}

function sumIncludedCombinedMonthly(combinedProducts: FinancingAiAddon[]): number {
    return Number(
        combinedProducts
            .filter((p) => p.included)
            .reduce((sum, p) => sum + p.monthly, 0)
            .toFixed(2),
    );
}

/** True when the selected header should show one-time cash instead of a monthly total. */
export function selectedPlanPriceIsCash(
    plan: FinancingAiPlan | null | undefined,
    combinedProducts: FinancingAiAddon[],
): boolean {
    if (!plan?.isCash) return false;
    return !combinedProducts.some((p) => p.included);
}

export function selectedPlanDisplayMonthly(
    plan: FinancingAiPlan,
    combinedProducts: FinancingAiAddon[],
): number {
    const fromIncluded = sumIncludedCombinedMonthly(combinedProducts);
    if (combinedProducts.some((p) => p.included) && fromIncluded > 0) {
        return fromIncluded;
    }
    if (plan.isCash) return plan.cashPrice ?? plan.monthly;
    return plan.monthly;
}

/** 'Solar + Battery' → ['Solar', 'Battery'] so compound and atomic columns dedupe against each other. */
function atomicProductParts(product: FinancingProduct): string[] {
    return String(product)
        .split('+')
        .map((part) => part.trim())
        .filter(Boolean);
}

/**
 * Products the displayed monthly price actually covers. `selectedPlanDisplayMonthly` sums every
 * included combined line, so the label has to follow the same lines rather than the plan's own
 * single product column — otherwise a Solar plan reads "Solar" while charging for Roof/Battery too.
 */
export function selectedPlanCoveredProducts(
    plan: FinancingAiPlan,
    combinedProducts: FinancingAiAddon[],
): string[] {
    const planParts = atomicProductParts(plan.product);
    const included = combinedProducts.filter((p) => p.included);
    if (included.length === 0) return planParts;

    const ordered: string[] = [];
    const seen = new Set<string>();
    for (const line of included) {
        for (const tag of line.productTags) {
            for (const part of atomicProductParts(tag)) {
                const key = part.toLowerCase();
                if (seen.has(key)) continue;
                seen.add(key);
                ordered.push(part);
            }
        }
    }
    return ordered.length > 0 ? ordered : planParts;
}

/** Splits combined lines into the bill-offsetting payment (solar/battery) and the roof payment. */
export function splitAddonMonthlyByBillOffset(products: FinancingAiAddon[]): {
    energy: number;
    nonEnergy: number;
} {
    let energy = 0;
    let nonEnergy = 0;
    for (const product of products) {
        if (product.productTags.some(productOffsetsUtilityBill)) energy += product.monthly;
        else nonEnergy += product.monthly;
    }
    return { energy, nonEnergy };
}

export function savingsForMonthlyPayment(
    utilityBillMonthly: number | null,
    monthlyPayment: number,
): {
    monthlySavings: number | null;
    year1Savings: number | null;
    savings25yr: number | null;
    cumulativeThisPlan: number | null;
} {
    if (utilityBillMonthly == null || utilityBillMonthly <= 0) {
        return {
            monthlySavings: null,
            year1Savings: null,
            savings25yr: null,
            cumulativeThisPlan: null,
        };
    }

    const monthlySavings = Math.max(0, utilityBillMonthly - monthlyPayment);
    const year1Savings = monthlySavings * 12;
    const savings25yr = year1Savings * 25;
    const cumulativeThisPlan = monthlyPayment * 12 * 25;

    return { monthlySavings, year1Savings, savings25yr, cumulativeThisPlan };
}

export function formatFinancingAiProductPrice(addon: FinancingAiAddon): string {
    return formatAddonPrice(addon);
}

/** Prefer grid-cell picks, else the first ranked partner plan. */
export function defaultSelectedPlanId(
    plans: FinancingAiPlan[],
    selectedCellKeys?: Iterable<string> | null,
): string {
    const keys = selectedCellKeys ? new Set(Array.from(selectedCellKeys)) : new Set<string>();
    if (keys.size > 0) {
        const match = plans.find(
            (plan) => keys.has(plan.cellKey) || keys.has(`${plan.partner}::${plan.product}`),
        );
        if (match) return match.id;
    }
    return plans[0]?.id ?? '';
}
